import { useState, useMemo, useEffect } from "react";
import { useQuery } from "react-query";
import { Container, Form } from "react-bootstrap";
import Spinner from "@components/Spinner/Spinner";
import ServerError from "@components/Error/ServerError";
import { useTranslation } from "react-i18next";
import { useProductionFilter } from "./ProductionFilterContext";
import UsageDistributionChart from "./UsageDistributionChart";
import ProductionVolumeChart from "./ProductionVolumeChart";
import CurrentStockChart from "./CurrentStockChart";
import { getProductionSensors } from "./api";
import { GREEN, YELLOW, RED, GREY, BLUE, shadeColor } from "@helper/colors";
import "./Dashboard.scss";

const CHART_COLORS = [
  GREEN,
  BLUE,
  YELLOW,
  RED,
  shadeColor(BLUE, 20),
  GREY,
  shadeColor(GREEN, -20),
  shadeColor(RED, -20),
];

export default function Dashboard() {
  const { t } = useTranslation();
  const { selectedYear, setSelectedYear, selectedQuarter, setSelectedQuarter, startDate, endDate } =
    useProductionFilter();

  // Fetch from the dedicated backend endpoint
  const {
    data: sensorsData,
    isLoading,
    isError,
  } = useQuery("production-dashboard", getProductionSensors, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const allSensors = Array.isArray(sensorsData)
    ? sensorsData
    : sensorsData?.results || sensorsData?.data || [];

  const [onlyQcPassed, setOnlyQcPassed] = useState(true);

  const filteredSensors = useMemo(() => {
    return allSensors.filter((sensor) => {
      if (onlyQcPassed && sensor.qc_passed !== true) return false;
      if (!sensor.produced_at) return false;
      if (startDate && endDate) {
        const producedAt = new Date(sensor.produced_at);
        return producedAt >= startDate && producedAt <= endDate;
      }
      return true;
    });
  }, [allSensors, startDate, endDate, onlyQcPassed]);

  const currentStockSensors = useMemo(() => {
    const cutoffDate = new Date("2026-06-15T00:00:00");
    return allSensors.filter((sensor) => {
      if (sensor.is_assigned !== false) return false;
      if (sensor.qc_passed !== true) return false;
      if (!sensor.produced_at) return false;
      return new Date(sensor.produced_at) >= cutoffDate;
    });
  }, [allSensors]);

  useEffect(() => {
    console.log("Filtered Dashboard Sensors:", filteredSensors);
    console.log("Current Stock Sensors:", currentStockSensors);
  }, [filteredSensors, currentStockSensors]);

  const colorMap = useMemo(() => {
    const predefinedColors = {
      "pulse-n": GREEN,
      "pulse n": GREEN,
      pulse_n: GREEN,
      "pulse-r": "#00A896", // Teal
      "pulse r": "#00A896",
      pulse_r: "#00A896",
      "pulse-s": "#9B5DE5", // Purple
      "pulse s": "#9B5DE5",
      pulse_s: "#9B5DE5",
      pulse: GREEN,
      dendro: YELLOW,
      watermark: BLUE,
      fdr: RED,
      soil_moisture: RED, // fallback
      teros: GREY,
    };

    const models = new Set();
    allSensors.forEach((s) => {
      if (s.sensor_type_key) models.add(s.sensor_type_key);
      else models.add("Unknown");
    });

    const uniqueModels = Array.from(models).sort();
    const map = {};
    let fallbackIndex = 0;

    uniqueModels.forEach((model) => {
      const lowerModel = model.toLowerCase();
      let matchedColor = null;

      for (const [key, color] of Object.entries(predefinedColors)) {
        if (lowerModel.includes(key)) {
          matchedColor = color;
          break;
        }
      }

      if (matchedColor) {
        map[model] = matchedColor;
      } else {
        map[model] = CHART_COLORS[fallbackIndex % CHART_COLORS.length];
        fallbackIndex++;
      }
    });
    return map;
  }, [allSensors]);

  if (isLoading) return <Spinner height={"80vh"} />;
  if (isError) return <ServerError />;

  const currentYearVal = new Date().getFullYear();
  const years = [];
  for (let y = 2023; y <= currentYearVal; y++) {
    years.push(y);
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <Container fluid>
          <div className="dashboard-header-inner">
            <div className="dashboard-subtitle">
              <h2 className="mb-0">{t("dashboard.title")}</h2>
            </div>
            <div className="dashboard-controls d-flex gap-3 align-items-center">
              <Form.Select
                value={selectedYear}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "all") {
                    setSelectedYear("all");
                    setSelectedQuarter("all");
                  } else {
                    setSelectedYear(parseInt(val));
                  }
                }}
                className="w-auto"
              >
                <option value="all">{t("dashboard.all_time")}</option>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Form.Select>

              <Form.Select
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                disabled={selectedYear === "all"}
                className="w-auto"
              >
                <option value="all">{t("dashboard.all_quarters")}</option>
                <option value="Q1">Q1</option>
                <option value="Q2">Q2</option>
                <option value="Q3">Q3</option>
                <option value="Q4">Q4</option>
              </Form.Select>

              <div className="dashboard-control dashboard-switch-control">
                <Form.Check
                  type="switch"
                  id="qc-passed-only-switch"
                  label={t("dashboard.qc_passed_only")}
                  checked={onlyQcPassed}
                  onChange={(e) => setOnlyQcPassed(e.target.checked)}
                />
              </div>
            </div>
          </div>
        </Container>
      </div>

      <div className="dashboard-content">
        <Container fluid>
          {/*
          <div className="dashboard-kpi-groups">
            <Card className="dashboard-panel dashboard-kpi-group flex-fill">
              <Card.Body>
                <div className="dashboard-kpi-group-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                  <KpiItem 
                    title="dashboard.total_produced"
                    value={totalProduced}
                    icon={<SensorsIcon size="20px" color="#ffffff" />}
                  />
                  <KpiItem 
                    title="dashboard.qc_passed_unassigned"
                    value={qcPassedUnassigned}
                    icon={<ClipboardDataIcon size="20px" color="#ffffff" />}
                  />
                </div>
              </Card.Body>
            </Card>
          </div>
          */}

          <div className="dashboard-panels">
            <div>
              <ProductionVolumeChart data={filteredSensors} colorMap={colorMap} />
            </div>

            <div className="dashboard-panel-stack">
              <UsageDistributionChart data={filteredSensors} />
              <CurrentStockChart data={currentStockSensors} colorMap={colorMap} />
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
}
